import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField, SearchInput } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { ITcProductRawMaterialInformation } from "../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { useFormActions } from "../../../../../../shared/hooks/form-builder.hooks";
import { dtrackItService } from "../../../tc-service/dtrackit.service";
import { INonCertifiedTrader } from "../../../tc-models/tc-req-dto/tc-buyer-seller-details.req_dto";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useParams } from "react-router-dom";
import { buyerSellerDetailsService } from "../../../tc-service/buyer-seller-details.service";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { RestrictedDataValidation } from "../../../../../../shared/utils/custom-validation.utils";

interface NonCertfiedInfoProps {
  nonCertifiedInfoData: INonCertifiedTrader[]; // Expecting an array
  setNonCertifiedInfoData: React.Dispatch<
    React.SetStateAction<INonCertifiedTrader[]>
  >;
}

// Define Yup validation schema for each row
const validationSchema = Yup.object().shape({
  sellerNonCertifiedTraderName: Yup.string()
    .nullable()
    .test('no-restricted-data', 'N/A, Not available, n/a are not allowed. Use "na" if not applicable', 
      (value) => {
        if (!value) return true;
        const trimmedValue = value.trim();
        // Allow "na" as it's a valid value for "not applicable"
        if (trimmedValue === "na") return true;
        // Use the imported function to check for other restricted values
        return !RestrictedDataValidation.isRestrictedData(value);
      }),
  sellerNonCertifiedTraderTeId: Yup.string().nullable(),
});

const NonCertifiedTraderInfo: React.FC<NonCertfiedInfoProps> = ({
  nonCertifiedInfoData,
  setNonCertifiedInfoData,
}) => {
  const { TE_ID, formtype } = useParams<string>();
  const hasSubmitTC: boolean = formtype === 'submited-tc' || formtype === 'view-etc';
  const [data, setData] = useState<INonCertifiedTrader[]>(
    nonCertifiedInfoData
  ); // Initialize with prop data
  const [tableData, setTableData] = useState<INonCertifiedTrader[]>(
    nonCertifiedInfoData
  ); // Initialize with prop data
  const [isTELabelEnabled, setTELabelEnabled] = useState(true);
  const formRef = useRef<any>(null);
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const { setRestrictPopUpSave, isRestrictPopUpSave } = useTransactionCertificateStore();
  
  const formik = useFormik<INonCertifiedTrader>({
    initialValues: {
      sellerNonCertifiedTraderName: "",
      sellerNonCertifiedTraderTeId: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  useEffect(()=>{
    setRestrictPopUpSave(false);
  },[]);
  const handleCellChange = (
    rowIndex: number,
    columnId: string,
    value: any
  ) => {
    formik.setFieldValue(columnId, value);
    setData((prevData) =>
      prevData.map((row, index) =>
        index === rowIndex ? { ...row, [columnId]: value } : row
      )
    );
  };

  const handleAddNewRow = () => {
    const newRow: INonCertifiedTrader = {
      sellerNonCertifiedTraderName: "",
      sellerNonCertifiedTraderTeId: ""
    };
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
    setRestrictPopUpSave(true);
  };

  const handleCancelEdit = async () => {
    setData(tableData);
    setRestrictPopUpSave(false);
    // Reset editing state and form
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = async () => {
    if (editingRowIndex === null) return;


    const updatedRow = formik.values;
    const newData = data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    setData(newData);
    setTableData(newData);
    setNonCertifiedInfoData(newData); // Update parent with current data
    setEditingRowIndex(-1);
    setRestrictPopUpSave(false);
    formik.resetForm();

  };
  const handleSellerNonCertifiedTraderTeIdChange = (value: any) => {
    const updatedData = [...data];

    if (editingRowIndex !== -1) {
      updatedData[editingRowIndex] = {
        ...updatedData[editingRowIndex],
        sellerNonCertifiedTraderTeId: value ? value.TE_ID : "",
        sellerNonCertifiedTraderName: value ? value.account_name : "",
      };

      setData(updatedData);
      setNonCertifiedInfoData(updatedData);
    }
    setTELabelEnabled(value===null || value?.TE_ID===null);
    formik.setFieldValue("sellerNonCertifiedTraderTeId", value ? value.TE_ID : "");
    formik.setFieldValue("sellerNonCertifiedTraderName", value ? value.account_name : "");
  };

  const fetchTEDetallsFromAPI = async (
    value: string,
    offset: number,
    type: string,
    name?: string
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        query_param: type == "query" ? value : "",
        offset: offset,
        name: type == "name" ? value : "",
      };
      // Fetch TE Details using the getTEDetails function
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      const data = response.data;

      return data;
    } catch (error) {
      console.error("Error fetching TE details:", error);
      throw error; // Re-throw error to handle it in the calling code
    }
  };
  const columns: EditableColumnDef<INonCertifiedTrader>[] = [
    {
      accessorKey: "sellerNonCertifiedTraderTeId",
      header: "Seller non-certified trader TE-ID",
      renderEditCell: ({ value, onChange }) => (
        <SearchInput
          placeholder="Search Seller non-certified trader TE-ID"
          param="SellerTEId"
          name="sellerNonCertifiedTraderTeId"
          pageName="NonCertifiedTraderInfo"
          onChange={handleSellerNonCertifiedTraderTeIdChange}
          value={value}
          restrictInputFormat={isTELabelEnabled || value===null}
          displayKey="TE_ID"
          onSearch={(query: string, offset: number) => fetchTEDetallsFromAPI(query, offset, "query")}
          fullWidth={true}
        />
      ),
    },
    {
      accessorKey: "sellerNonCertifiedTraderName",
      header: "Legal name of non-certified trader",
      renderEditCell: ({ value, onChange }) => (
        <CustomTextField
          name="sellerNonCertifiedTraderName"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter value"
          sx={{ mb: 2 }}
          error={Boolean(formik.errors.sellerNonCertifiedTraderName && formik.touched.sellerNonCertifiedTraderName)}
          helperText={formik.touched.sellerNonCertifiedTraderName && formik.errors.sellerNonCertifiedTraderName}
        />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    formik.setValues(data[rowIndex]);
    const teIdValue = data[rowIndex]?.sellerNonCertifiedTraderTeId;
    setTELabelEnabled(teIdValue===null);
    setRestrictPopUpSave(true);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setNonCertifiedInfoData(updatedData); // Update parent with updated data
  };

  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          {!hasSubmitTC && <StyledButton onClick={handleAddNewRow} sx={{ mb: 2 }} disabled={!formik.isValid || isRestrictPopUpSave}>
            Add Non Certified Trader Info
          </StyledButton>}
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
          enableEditing={!hasSubmitTC}
        />
      </Box>
    </div>
  );
};

export default NonCertifiedTraderInfo;
